import { useState, useEffect, useCallback, useRef, memo } from 'react';
import { Search, X, ExternalLink } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface SearchSuggestion {
  id: string | number;
  /** Primary text shown in the suggestion (e.g., picklist number) */
  title: string;
  /** Secondary text shown below the title */
  subtitle?: string;
  /** URL to open when the suggestion is clicked */
  href: string;
}

interface SearchSuggestionsProps {
  /** Function to filter/search and return suggestions for the given query */
  getSuggestions: (query: string) => SearchSuggestion[];
  /** Debounce delay in milliseconds (default: 300ms) */
  delay?: number;
  /** Placeholder text */
  placeholder?: string;
  /** Max suggestions to display (default: 8) */
  maxSuggestions?: number;
  /** Additional class names for the container */
  className?: string;
  /** Additional class names for the input */
  inputClassName?: string;
}

/**
 * A search input that shows matching suggestions in a dropdown.
 * Suggestions open their target URL in a new browser tab.
 * Does NOT affect any external table/list state.
 */
export const SearchSuggestions = memo(function SearchSuggestions({
  getSuggestions,
  delay = 300,
  placeholder = 'Search...',
  maxSuggestions = 8,
  className,
  inputClassName,
}: SearchSuggestionsProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce the query
  useEffect(() => {
    if (!query.trim()) {
      setDebouncedQuery('');
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, delay);
    return () => clearTimeout(timer);
  }, [query, delay]);

  // Compute suggestions from debounced query
  const suggestions = debouncedQuery.trim()
    ? getSuggestions(debouncedQuery).slice(0, maxSuggestions)
    : [];

  // Open dropdown when there are suggestions
  useEffect(() => {
    setIsOpen(suggestions.length > 0);
    setHighlightedIndex(-1);
  }, [suggestions.length]);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  }, []);

  const handleClear = useCallback(() => {
    setQuery('');
    setDebouncedQuery('');
    setIsOpen(false);
    inputRef.current?.focus();
  }, []);

  const handleSuggestionClick = useCallback((suggestion: SearchSuggestion) => {
    window.open(suggestion.href, '_blank', 'noopener,noreferrer');
    setIsOpen(false);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen || suggestions.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
            handleSuggestionClick(suggestions[highlightedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          inputRef.current?.blur();
          break;
      }
    },
    [isOpen, suggestions, highlightedIndex, handleSuggestionClick]
  );

  const handleFocus = useCallback(() => {
    if (suggestions.length > 0) {
      setIsOpen(true);
    }
  }, [suggestions.length]);

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          className={cn('pl-8', query && 'pr-8', inputClassName)}
          autoComplete="off"
        />
        {query && (
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
            onClick={handleClear}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg">
          <ul className="max-h-64 overflow-y-auto py-1" role="listbox">
            {suggestions.map((suggestion, index) => (
              <li
                key={suggestion.id}
                role="option"
                aria-selected={index === highlightedIndex}
                className={cn(
                  'flex items-center justify-between gap-2 px-3 py-2 cursor-pointer text-sm',
                  index === highlightedIndex
                    ? 'bg-accent text-accent-foreground'
                    : 'hover:bg-accent/50'
                )}
                onMouseEnter={() => setHighlightedIndex(index)}
                onMouseDown={(e) => {
                  // Prevent input blur before click fires
                  e.preventDefault();
                  handleSuggestionClick(suggestion);
                }}
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{suggestion.title}</p>
                  {suggestion.subtitle && (
                    <p className="text-xs text-muted-foreground truncate">
                      {suggestion.subtitle}
                    </p>
                  )}
                </div>
                <ExternalLink className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
});
